import { Injectable } from '@angular/core';
import {
  CanActivate,
  CanActivateChild,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthenticationService } from '../services/authentication.service';
import { firstAllowedRoute, Role } from './route-roles';

/**
 * Guard phân quyền dùng chung, data-driven.
 *
 * Ưu tiên `route.data.feature` → tra quyền XEM theo VỊ TRÍ của tài khoản (đọc
 * từ collection `position`, Admin chỉnh ở /fox-admin/settings/position).
 * Vẫn chấp nhận `route.data.roles` kiểu cũ cho route chưa chuyển đổi.
 * Không khai báo gì → mặc định mọi tài khoản đã đăng nhập.
 */
@Injectable()
export class RoleGuard implements CanActivate, CanActivateChild {
  constructor(
    private router: Router,
    private auth: AuthenticationService,
    private message: NzMessageService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.check(route, state);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.check(route, state);
  }

  private check(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const feature = route.data['feature'] as string | undefined;
    const roles = route.data['roles'] as Role[] | undefined;
    // Không khai báo gì (vd route cha 'settings' chỉ bọc canActivateChild) thì
    // chỉ cần còn phiên đăng nhập — KHÔNG so với ['1','2','3'], vì vị trí tự
    // tạo có id bất kỳ và sẽ bị chặn oan.
    const allowed$ = feature
      ? this.auth.canAccessFeature(feature)
      : roles
      ? this.auth.canAccess(roles)
      : this.auth.isAuthenticated();

    return allowed$.pipe(
      tap((allowed) => {
        if (allowed) return;
        this.message.error('Bạn không có quyền truy cập.');
        // Hết phiên đăng nhập → về trang đăng nhập. Còn đăng nhập mà vị trí
        // không có quyền → đưa về trang ĐẦU TIÊN vị trí đó mở được.
        //
        // Không đá về '/fox-admin' nữa: đó chính là Dashboard, mà vị trí như
        // Nhân viên không có quyền xem Dashboard → guard chặn tiếp → đẩy ra
        // login → khoá luôn người dùng ở ngoài dù tài khoản hoàn toàn hợp lệ.
        const stillLoggedIn = this.auth.isLogin();
        const fallback = stillLoggedIn ? firstAllowedRoute() : null;
        if (fallback && fallback !== state.url) {
          this.router.navigate([fallback]);
        } else {
          this.router.navigate(['/fox-admin/login'], {
            queryParams: { returnUrl: state.url },
          });
        }
      })
    );
  }
}
